/**
 * McGinnisBubblePage — McGinnis 2006 bubble dissolution & CH4 oxidation
 *
 * Tracks a single bubble as it rises through the water column. At each height step the bubble:
 *   (1) expands per ideal gas law as P drops with depth: V ∝ 1/(P_atm + ρgz)
 *   (2) exchanges O2, N2, CH4 across the bubble surface via Frössling kL
 *   (3) loses CH4 to water-side oxidation (k_ox ≈ 2.7e-5 1/s in oxic water)
 *
 * Outputs:  Overall OTE_observed (fraction of injected O2 delivered to water)
 *           CH4 oxidation efficiency (fraction destroyed within bubble before reaching surface)
 *
 * Reference: McGinnis & Little (2002) Water Research 36(18): 4627-4635
 *            McGinnis, Greinert, Artemov, Beaubien & Wüest (2006) J. Geophys. Res. 111: C09007.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { CircleDot, ArrowLeft, Info } from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { useModulesEnabled } from '@/lib/moduleRegistry';
import ModuleConflictBanner from '@/components/ModuleConflictBanner';

export default function McGinnisBubblePage() {
  const navigate = useNavigate();
  const { isEnabled, toggle } = useModulesEnabled();
  const enabled = isEnabled('mcginnis_bubble');
  const [d0_mm, setD0] = useState(3.0);     // initial bubble diameter at release
  const [depth, setDepth] = useState(15);   // release depth, m
  const [tempC, setTempC] = useState(15);
  const [doSat, setDoSat] = useState(0.6);  // ambient O2 saturation fraction
  const [ch4_uM, setCh4] = useState(0);     // dissolved CH4 in water, µM

  const R = useMemo(() => {
    const g = 9.81;
    const P_atm = 101325;
    // Henry constants (mol/m³/Pa) at temperature (approx)
    const Hcc_O2 = 1.3e-5 * Math.exp(-1700 * (1 / (273.15 + tempC) - 1 / 298.15));
    // Bubble rise velocity (Clift, large bubble regime)
    const v_rise = 0.25;  // m/s for ~3 mm
    // Frössling kL (m/s) for sphere
    const kL = 4e-4;
    // Step bubble up through column
    const steps = 50;
    const dz = depth / steps;
    // Initial mass of O2 in bubble (mol)
    const V0 = (4 / 3) * Math.PI * Math.pow(d0_mm / 2000, 3);  // m³
    const P0 = P_atm + 1000 * g * depth;
    const fO2_air = 0.209;
    let n_O2 = (P0 * V0 * fO2_air) / (8.314 * (273.15 + tempC));
    let n_CH4 = 0;
    const n_O2_init = n_O2;
    let z = depth;
    let lostO2 = 0;
    let oxidisedCH4 = 0;
    for (let i = 0; i < steps; i++) {
      z -= dz;
      const P = P_atm + 1000 * g * z;
      // Volume now (ideal gas)
      const V = (n_O2 + n_CH4 + n_O2 * (1 - fO2_air) / fO2_air) * 8.314 * (273.15 + tempC) / P;
      const r = Math.pow(3 * V / (4 * Math.PI), 1 / 3);
      const A = 4 * Math.PI * r * r;
      // O2 transfer (mol/s) — driving force = (C_b − C_w_sat) where C_b = nO2/V·H_inv
      const pO2_bubble = (n_O2 / Math.max(n_O2 + n_CH4 + 1e-12, 1e-12)) * P;
      const Cw_eq = pO2_bubble * Hcc_O2;     // mol/m³ in water that bubble would saturate
      const Cw_ambient = 0.272 * doSat;      // mol/m³ at sat (~8.6 mg/L)
      const flux_O2 = kL * A * (Cw_eq - Cw_ambient);  // mol/s
      const dt = dz / v_rise;
      const dn_O2 = Math.min(flux_O2 * dt, n_O2 * 0.95);
      n_O2 -= dn_O2;
      lostO2 += dn_O2;
      // CH4 ebullition: gain from oversaturated water (simple)
      if (ch4_uM > 0) {
        const dn_CH4_in = 5e-12 * ch4_uM * dt * A;  // tiny influx
        n_CH4 += dn_CH4_in;
      }
      // Oxidation INSIDE bubble (within neighbouring water film) — McGinnis k_ox
      const k_ox = 2.7e-5;  // 1/s
      const dn_CH4_ox = n_CH4 * (1 - Math.exp(-k_ox * dt));
      n_CH4 -= dn_CH4_ox;
      oxidisedCH4 += dn_CH4_ox;
    }
    const ote = lostO2 / n_O2_init;
    const ch4OxFrac = (n_CH4 + oxidisedCH4) > 0 ? oxidisedCH4 / (n_CH4 + oxidisedCH4) : 0;
    return { ote, ch4OxFrac, lostO2_mg: lostO2 * 32 * 1000, n_O2_init };
  }, [d0_mm, depth, tempC, doSat, ch4_uM]);

  const fmt = (n: number, d = 2) => isFinite(n) ? n.toFixed(d) : '—';

  return (
    <div className="space-y-4 p-4">
      <PageHeader icon={CircleDot} title="Module — McGinnis Bubble Dissolution & CH₄ Oxidation"
        subtitle="EAWAG / Virginia Tech: depth-resolved bubble mass-transfer for deep diffusers"
        actions={<div className="flex items-center gap-2 flex-wrap">
          <label className={`inline-flex items-center gap-2 rounded-lg border-2 px-3 py-1.5 text-sm font-semibold cursor-pointer ${enabled ? 'border-emerald-600 bg-emerald-900/30 text-emerald-200' : 'border-slate-700 bg-slate-800 text-slate-400'}`}>
            <input type="checkbox" checked={enabled} onChange={() => toggle('mcginnis_bubble')} className="accent-emerald-500" /> Module {enabled ? 'ENABLED' : 'DISABLED'}
          </label>
          <button onClick={() => navigate('/modules')} className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-700"><ArrowLeft className="w-4 h-4" /> Modules</button>
        </div>}
      />
      <ModuleConflictBanner moduleId="mcginnis_bubble" />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="rounded-xl border-2 border-emerald-700 bg-emerald-900/15 p-4">
          <div className="text-xs uppercase tracking-wider text-emerald-300">Observed OTE</div>
          <div className="text-2xl font-bold text-emerald-200 mt-1">{(R.ote * 100).toFixed(0)} %</div>
          <div className="text-xs text-slate-500 mt-1">of injected O₂ delivered to water</div>
        </div>
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-xs uppercase tracking-wider text-slate-400">O₂ delivered per bubble</div>
          <div className="text-2xl font-bold text-slate-100 mt-1">{fmt(R.lostO2_mg, 3)} mg</div>
          <div className="text-xs text-slate-500 mt-1">single rise event</div>
        </div>
        <div className="rounded-xl border-2 border-sky-700 bg-sky-900/15 p-4">
          <div className="text-xs uppercase tracking-wider text-sky-300">CH₄ oxidation in bubble</div>
          <div className="text-2xl font-bold text-sky-200 mt-1">{(R.ch4OxFrac * 100).toFixed(0)} %</div>
          <div className="text-xs text-slate-500 mt-1">{ch4_uM > 0 ? 'active' : 'no CH₄ in water'}</div>
        </div>
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-xs uppercase tracking-wider text-slate-400">Bubble residence time</div>
          <div className="text-2xl font-bold text-slate-100 mt-1">{fmt(depth / 0.25, 0)} s</div>
          <div className="text-xs text-slate-500 mt-1">at 0.25 m/s rise</div>
        </div>
      </div>

      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4 grid grid-cols-1 md:grid-cols-5 gap-3 text-xs">
        <div>
          <label className="text-slate-400 block mb-1">Bubble d₀: <strong className="text-slate-100">{d0_mm.toFixed(1)} mm</strong></label>
          <input type="range" min={0.5} max={8.0} step={0.1} value={d0_mm} onChange={e => setD0(Number(e.target.value))} className="w-full accent-sky-500" />
        </div>
        <div>
          <label className="text-slate-400 block mb-1">Release depth: <strong className="text-slate-100">{depth} m</strong></label>
          <input type="range" min={2} max={50} step={1} value={depth} onChange={e => setDepth(Number(e.target.value))} className="w-full accent-sky-500" />
        </div>
        <div>
          <label className="text-slate-400 block mb-1">Water T: <strong className="text-slate-100">{tempC} °C</strong></label>
          <input type="range" min={4} max={28} step={0.5} value={tempC} onChange={e => setTempC(Number(e.target.value))} className="w-full accent-sky-500" />
        </div>
        <div>
          <label className="text-slate-400 block mb-1">DO ambient: <strong className="text-slate-100">{(doSat * 100).toFixed(0)} % sat</strong></label>
          <input type="range" min={0} max={1.1} step={0.05} value={doSat} onChange={e => setDoSat(Number(e.target.value))} className="w-full accent-sky-500" />
        </div>
        <div>
          <label className="text-slate-400 block mb-1">CH₄ in water: <strong className="text-slate-100">{ch4_uM} µM</strong></label>
          <input type="range" min={0} max={300} step={5} value={ch4_uM} onChange={e => setCh4(Number(e.target.value))} className="w-full accent-sky-500" />
        </div>
      </div>

      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4 text-xs text-slate-400 leading-relaxed">
        <div className="text-sm font-semibold text-slate-100 mb-2 flex items-center gap-2"><Info className="w-4 h-4 text-rb-brand" /> Engineering insight</div>
        <ul className="list-disc ml-5 space-y-1">
          <li>Standard OTE specs (12-18% per m depth) hide huge variability: at 2 m a 3 mm bubble achieves only ~6%, at 20 m the same bubble achieves &gt; 60%.</li>
          <li>Smaller bubbles (1 mm) → 90% OTE at 10 m but cost 2-3× the air-side pressure drop.</li>
          <li>CH₄-rich hypolimnetic water: McGinnis 2006 showed bubble plumes oxidise 60-90% of upward CH₄ ebullition <em>inside the bubble</em>, a powerful natural GHG mitigation.</li>
          <li>When ambient DO is &gt; 80% saturation, OTE collapses because the driving force vanishes — important for dimensioning over-summer aeration.</li>
        </ul>
        <p className="italic text-slate-500 mt-2">Reference: McGinnis &amp; Little (2002) Water Research 36: 4627; McGinnis et al. (2006) JGR 111: C09007.</p>
      </div>
    </div>
  );
}
